import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Loader2, RotateCcw, Play } from 'lucide-react';
import * as XLSX from 'xlsx';
import { supabase } from '@/integrations/supabase/client';
import { paginateQuery } from '@/lib/paginateSupabase';
import { getFunctionErrorMessage } from '@/lib/functionError';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const REC_LABEL: Record<string, string> = { sent: 'Enviado', failed: 'Falló', pending: 'Pendiente', skipped: 'Omitido' };
const PAGE = 50;

export function BulkCampaignDetailDialog({ campaign, onClose }: { campaign: any | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(0);
  const [confirm, setConfirm] = useState<null | 'retry_failed' | 'resume'>(null);
  const [busy, setBusy] = useState(false);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['bulk-recipients', campaign?.id],
    enabled: !!campaign,
    queryFn: () => paginateQuery<any>((a, b) => supabase.from('bulk_email_recipients')
      .select('id, email, full_name, status, error, sent_at').eq('campaign_id', campaign.id).range(a, b)),
    refetchInterval: campaign?.status === 'sending' ? 5000 : false,
  });

  const counts = useMemo(() => {
    const c: Record<string, number> = { sent: 0, failed: 0, pending: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] ?? 0) + 1; });
    return c;
  }, [rows]);
  const total = rows.length || campaign?.total_recipients || 0;

  const filtered = useMemo(() => rows
    .filter((r) => status === 'all' || r.status === status)
    .filter((r) => !q || `${r.full_name ?? ''} ${r.email}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (a.full_name ?? a.email).localeCompare(b.full_name ?? b.email)), [rows, status, q]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const visible = filtered.slice(page * PAGE, page * PAGE + PAGE);

  const run = async () => {
    if (!confirm) return;
    setBusy(true);
    const { data, error } = await supabase.functions.invoke('bulk-email-send', { body: { mode: confirm, campaign_id: campaign.id } });
    setBusy(false);
    setConfirm(null);
    if (error || data?.error) { toast.error(data?.error ?? await getFunctionErrorMessage(error)); return; }
    toast.success(confirm === 'retry_failed' ? `Reenviando a ${data.reset} persona(s).` : 'Envío reanudado.');
    qc.invalidateQueries({ queryKey: ['bulk-campaigns'] });
    qc.invalidateQueries({ queryKey: ['bulk-recipients', campaign.id] });
  };

  const exportXlsx = () => {
    const ws = XLSX.utils.json_to_sheet(filtered.map((r) => ({
      Nombre: r.full_name ?? '', Correo: r.email, Estado: REC_LABEL[r.status] ?? r.status,
      'Enviado el': r.sent_at ? new Date(r.sent_at).toLocaleString('es-CO') : '', Error: r.error ?? '',
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Destinatarios');
    XLSX.writeFile(wb, `comunicado-${campaign.id.slice(0, 8)}.xlsx`);
  };

  const sending = campaign?.status === 'sending';

  return (
    <Dialog open={!!campaign} onOpenChange={(o) => { if (!o) { onClose(); setQ(''); setStatus('all'); setPage(0); } }}>
      <DialogContent className="max-w-4xl">
        <DialogHeader><DialogTitle className="pr-6">{campaign?.subject}</DialogTitle></DialogHeader>

        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2 text-center text-sm md:grid-cols-4">
            <div className="rounded-md border border-border p-2"><p className="text-lg font-semibold">{total}</p><p className="text-muted-foreground">Total</p></div>
            <div className="rounded-md border border-border p-2"><p className="text-lg font-semibold text-primary">{counts.sent}</p><p className="text-muted-foreground">Enviados</p></div>
            <div className="rounded-md border border-border p-2"><p className="text-lg font-semibold text-destructive">{counts.failed}</p><p className="text-muted-foreground">Fallidos</p></div>
            <div className="rounded-md border border-border p-2"><p className="text-lg font-semibold">{counts.pending}</p><p className="text-muted-foreground">Pendientes</p></div>
          </div>
          <Progress value={total ? ((counts.sent + counts.failed) / total) * 100 : 0} />
          {campaign?.last_error && <p className="text-xs text-destructive">{campaign.last_error}</p>}
          <div className="flex flex-wrap gap-2">
            {counts.failed > 0 && !sending && (
              <Button size="sm" variant="destructive" onClick={() => setConfirm('retry_failed')}><RotateCcw className="mr-2 h-4 w-4" />Reenviar fallidos ({counts.failed})</Button>
            )}
            {counts.pending > 0 && !sending && (
              <Button size="sm" onClick={() => setConfirm('resume')}><Play className="mr-2 h-4 w-4" />Reanudar envío</Button>
            )}
            {sending && <Badge variant="secondary"><Loader2 className="mr-1 h-3 w-3 animate-spin" />Enviando…</Badge>}
            <Button size="sm" variant="outline" onClick={exportXlsx} disabled={!rows.length}><Download className="mr-2 h-4 w-4" />Exportar a Excel</Button>
          </div>
        </div>

        <Tabs defaultValue="recipients">
          <TabsList><TabsTrigger value="recipients">Destinatarios</TabsTrigger><TabsTrigger value="content">Contenido</TabsTrigger></TabsList>
          <TabsContent value="recipients" className="space-y-2">
            <div className="flex flex-col gap-2 md:flex-row">
              <Input placeholder="Buscar por nombre o correo" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} />
              <Select value={status} onValueChange={(v) => { setStatus(v); setPage(0); }}>
                <SelectTrigger className="md:w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="sent">Enviados</SelectItem>
                  <SelectItem value="failed">Fallidos</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="max-h-[45vh] overflow-auto rounded-md border border-border">
              {isLoading ? <p className="p-4 text-sm text-muted-foreground">Cargando…</p> : (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted text-left"><tr>
                    <th className="p-2">Nombre</th><th className="p-2">Correo</th><th className="p-2">Estado</th><th className="p-2">Enviado</th><th className="p-2">Motivo</th>
                  </tr></thead>
                  <tbody className="divide-y divide-border">
                    {visible.map((r) => (
                      <tr key={r.id}>
                        <td className="p-2">{r.full_name ?? '—'}</td>
                        <td className="p-2 break-all">{r.email}</td>
                        <td className="p-2"><Badge variant={r.status === 'sent' ? 'default' : r.status === 'failed' ? 'destructive' : 'secondary'}>{REC_LABEL[r.status] ?? r.status}</Badge></td>
                        <td className="p-2 whitespace-nowrap">{r.sent_at ? new Date(r.sent_at).toLocaleString('es-CO') : '—'}</td>
                        <td className="p-2 text-xs text-destructive max-w-[220px] break-words">{r.error ?? ''}</td>
                      </tr>
                    ))}
                    {!visible.length && <tr><td colSpan={5} className="p-4 text-muted-foreground">Sin resultados</td></tr>}
                  </tbody>
                </table>
              )}
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{filtered.length} persona(s)</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)}>Anterior</Button>
                <span>{page + 1} / {pages}</span>
                <Button size="sm" variant="outline" disabled={page >= pages - 1} onClick={() => setPage(page + 1)}>Siguiente</Button>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="content">
            <div className="max-h-[50vh] overflow-auto rounded-md border border-border p-4 text-sm [&_a]:text-primary [&_ul]:list-disc [&_ul]:pl-5 [&_p]:mb-3"
              dangerouslySetInnerHTML={{ __html: campaign?.html_body ?? '' }} />
          </TabsContent>
        </Tabs>

        <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{confirm === 'retry_failed' ? '¿Reenviar a los fallidos?' : '¿Reanudar el envío?'}</AlertDialogTitle>
              <AlertDialogDescription>
                {confirm === 'retry_failed'
                  ? `Se enviará de nuevo solo a las ${counts.failed} persona(s) que fallaron. Quienes ya lo recibieron no lo recibirán otra vez.`
                  : `Se enviará a las ${counts.pending} persona(s) pendientes.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { e.preventDefault(); run(); }} disabled={busy}>
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Confirmar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
